import { useState, useEffect, ChangeEvent } from 'react';
import { Col, Row, Toast, ToastContainer } from 'react-bootstrap';

import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import useAdvanceTable from 'hooks/useAdvanceTable';
import DatePicker from 'components/base/DatePicker';
import {
    faFileExport,
    faFilter,
    faUserPlus
} from '@fortawesome/free-solid-svg-icons';
import SearchBox from 'components/common/SearchBox';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'providers/AuthProvider'
import apiClient, { handleAPIError } from 'helpers/apiService';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import Button from 'components/base/Button';
import LeadsFilterModal from 'components/modals/LeadsFilterModal';


export interface Report {
    resource_id: number;
    resource_name: string;
    meeting_recording_name: string;
    meeting_recording_id: number;
    inserted_at: string;
}

const ReportsTable = () => {
    return (
        <div className="border-top">
            <AdvanceTable
                tableProps={{ className: 'phoenix-table fs-9' }}
                rowClassName="hover-actions-trigger btn-reveal-trigger"
            />
            <AdvanceTableFooter pagination className="py-4" />
        </div>
    );
};


const FeedbackReports = () => {
    const { t } = useTranslation();

    const breadcrumbItems: PageBreadcrumbItem[] = [
        {
            label: t('Dashboard'),
            url: '/dashboard/home'
        },

        {
            label: t('Feedback'),
            url: '/feedback-dashboard'
        },
        {
            label: t('Reports'),
            active: true
        }
    ];


    const auth = useAuth();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);

    const [openFilterModal, setOpenFilterModal] = useState(false);
    const handleSearchInputChange = (e: ChangeEvent<HTMLInputElement>) => {
        table.setGlobalFilter(e.target.value || undefined);
    };

    const [reports, setReports] = useState<Report[]>([]);

    // State to control Toast messages
    const [show, setShow] = useState(false);
    const [toastMessage, setToastMessage] = useState('');

    const handleClick = async (resource_id: number) => {
        try {
            const response = await apiClient.get(
                `/v1/reports/${resource_id}`,
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );

            const pdfUrl = response.data.pdf_url;
            window.open(pdfUrl, '_blank');
        } catch (error) {
            console.error('Error fetching PDF link:', error);
        }
    };

    const certificateListTableColumns: ColumnDef<Report>[] = [
        {
            header: t('Report.table.title'),
            cell: ({ row: { original } }) => {
                const { resource_id, resource_name } = original;
                return (
                    <Link to='#!' onClick={() => handleClick(resource_id)} className="fw-semi-bold">
                        {resource_name}
                    </Link>
                );
            },
            meta: {
                cellProps: { className: 'white-space-nowrap py-4' },
                headerProps: { style: { width: '25%' } }
            }
        },

        {
            header: t('Report.table.meeting_recording'),
            cell: ({ row: { original } }) => {
                const { meeting_recording_id, meeting_recording_name } = original;
                return (
                    <Link to={`/effective-communication/${meeting_recording_id}`} className="fw-semi-bold">
                        {meeting_recording_name}
                    </Link>
                );
            },

            meta: {
                headerProps: { style: { width: '25%' }, className: 'ps-3' }
            }
        },

        {
            accessorKey: 'inserted_at',
            header: 'Date',
            meta: {
                cellProps: { className: 'ps-3 text-900 py-4' },
                headerProps: { style: { width: '12%' }, className: 'ps-3' }
            }
        },


    ];

    const table = useAdvanceTable({
        data: reports,
        columns: certificateListTableColumns,
        pageSize: 6,
        pagination: true,
        sortable: true
    });


    const fetchData = async () => {
        try {
            const response = await apiClient.get(
                '/v1/reports',
                {
                    headers: {
                        'accept': 'application/json',

                    },
                    withCredentials: true,
                }
            );
            //console.log(response.data);
            setReports(response.data);
        } catch (error) {
            console.error('Error fetching data:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);



    if (loading) {
        return <div>Loading...</div>;
    }

    return (
        <div>

            <PageBreadcrumb items={breadcrumbItems} />

            <div>
                <ToastContainer className="p-3" position='top-center'>
                    <Toast onClose={() => setShow(false)} show={show} delay={3000} autohide>
                        <Toast.Body className=''>{toastMessage}</Toast.Body>
                    </Toast>
                </ToastContainer>
            </div>
            <div className="mb-6">
                <AdvanceTableProvider {...table}>
                    <h2 className="mb-4">{t("Reports.title")}</h2>
                    <Row className="g-3 justify-content-between mb-4">
                        <Col xs="auto">
                        </Col>
                        <Col xs="auto">
                            <div className="d-flex">
                                <SearchBox
                                    placeholder="Search by name"
                                    className="me-2"
                                    onChange={handleSearchInputChange}
                                />
                                <DatePicker defaultValue="Mar 1, 2022" />
                                <Button
                                    variant="phoenix-secondary"
                                    className="px-3 ms-2"
                                    onClick={() => setOpenFilterModal(true)}
                                >
                                    <FontAwesomeIcon
                                        icon={faFilter}
                                        transform="down-3"
                                        className="text-primary"
                                    />
                                </Button>
                            </div>
                            <LeadsFilterModal
                                show={openFilterModal}
                                handleClose={() => setOpenFilterModal(false)}
                            />
                        </Col>
                    </Row>
                    <ReportsTable />
                </AdvanceTableProvider>
            </div>
        </div>
    );


};

export default FeedbackReports;
